import styled, { keyframes } from "styled-components";
import { ReactComponent as ChevronSvg } from "../../assets/icons/chevron-down.svg";

/* Soft breathing pulse for the open dropdown panel so it feels "alive". */
const panelFloat = keyframes`
  0%, 100% {
    filter: brightness(1);
  }
  50% {
    filter: brightness(1.02);
  }
`;

// ─── Container ──────────────────────────────────────────────────────────────

export const DropdownContainer = styled.div`
  display: inline-flex;
  width: ${(p) => (p.$fullWidth ? "100%" : "unset")};
`;

// ─── Trigger button — frosted-glass pill matching CategoryItem ─────────────

export const DropdownButton = styled.button`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  /* Match CollectionFilter CategoryChip footprint: 46px tall, 0 20px padding,
     same radial-gradient + box-shadow recipe so it lives in the same row. */
  min-height: 46px;
  height: 46px;
  padding: ${(p) => (p.$multipleSelect ? "0 16px" : "0 20px")};
  min-width: ${(p) => (p.$multipleSelect ? "46px" : "96px")};
  border-radius: var(--radius-pill);
  border: 1px solid rgba(var(--glass-white-rgb), 0.65);
  border: none;

  width: ${(p) => (p.$fullWidth ? "100%" : "fit-content")};
  background: radial-gradient(
    120% 100% at 20% 0%,
    rgba(var(--glass-white-rgb), 0.88) 0%,
    rgba(var(--glass-pale-rgb), 0.66) 55%,
    rgba(var(--glass-pale-rgb), 0.5) 100%
  );
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  box-shadow:
    0 1.5px 3px rgba(var(--color-brand-500-rgb), 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.92),
    inset 0 -2px 6px rgba(var(--color-brand-200-rgb), 0.2);

  [data-theme="dark"] & {
    border-color: transparent;
    box-shadow:
      0 2px 6px rgba(0, 0, 0, 0.35),
      inset 0 -2px 6px rgba(var(--color-brand-700-rgb), 0.14);
  }

  color: var(--cf-text);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.01em;
  line-height: normal;
  white-space: nowrap;
  cursor: pointer;
  transition:
    transform 220ms var(--ease-snappy),
    border-color 220ms var(--ease-snappy),
    background 220ms var(--ease-snappy),
    box-shadow 220ms var(--ease-snappy),
    color 220ms var(--ease-snappy);

  /* Top sheen — matches CategoryItem */
  &::before {
    content: "";
    position: absolute;
    top: 1px;
    left: 14px;
    right: 14px;
    height: 8px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.6) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  > span {
    position: relative;
    z-index: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 180px;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: translateY(-1px);
    }
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 480px) {
    min-height: ${({ $compactOnMobile }) =>
      $compactOnMobile ? "38px" : "42px"};
    padding: ${({ $compactOnMobile, $multipleSelect }) => {
      if (!$compactOnMobile) return $multipleSelect ? "0 14px" : "0 18px";
      return $multipleSelect ? "0 12px" : "0 14px";
    }};
    min-width: ${({ $compactOnMobile, $multipleSelect }) => {
      if (!$compactOnMobile) return $multipleSelect ? "42px" : "96px";
      return $multipleSelect ? "38px" : "84px";
    }};
    gap: ${({ $compactOnMobile }) => ($compactOnMobile ? "8px" : "10px")};
    font-size: ${({ $compactOnMobile }) =>
      $compactOnMobile ? "13px" : "14px"};
  }
`;

export const ChevronIcon = styled(ChevronSvg)`
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform: ${(props) => (props.$isOpen ? "rotate(180deg)" : "rotate(0deg)")};
  width: 14px;
  height: auto;
  flex-shrink: 0;
  position: relative;
  z-index: 1;

  path {
    stroke: var(--color-brand-600);
  }

  [data-theme="dark"] & path {
    stroke: var(--color-text-strong);
  }
`;

// ─── Floating panel — frosted water glass ──────────────────────────────────

export const DropdownMenu = styled.div`
  position: fixed;
  top: ${(props) => props.$top}px;
  left: ${(props) => props.$left}px;
  min-width: ${(props) => props.$width}px;
  max-height: calc(44px * 6);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  padding: 8px;
  gap: 2px;
  border-radius: 22px;
  border: 1px solid var(--glass-edge);
  overflow: hidden;
  overflow-y: auto;
  background:
    radial-gradient(
      120% 60% at 30% -10%,
      rgba(var(--glass-white-rgb), 0.95) 0%,
      rgba(var(--glass-pale-rgb), 0.85) 60%,
      rgba(var(--glass-pale-rgb), 0.75) 100%
    ),
    linear-gradient(
      180deg,
      rgba(var(--glass-white-rgb), 0.92) 0%,
      rgba(var(--glass-pale-rgb), 0.9) 100%
    );
  backdrop-filter: blur(28px) saturate(180%);
  -webkit-backdrop-filter: blur(28px) saturate(180%);
  box-shadow:
    0 28px 52px -14px rgba(var(--color-brand-500-rgb), 0.36),
    0 10px 26px rgba(var(--color-brand-400-rgb), 0.18),
    inset 0 2px 0 rgba(255, 255, 255, 0.95),
    inset 0 -10px 18px rgba(var(--color-brand-200-rgb), 0.26);

  [data-theme="dark"] & {
    box-shadow:
      0 24px 48px -14px rgba(0, 0, 0, 0.6),
      0 8px 20px rgba(0, 0, 0, 0.36),
      inset 0 1px 0 rgba(255, 255, 255, 0.06),
      inset 0 -10px 18px rgba(var(--color-brand-700-rgb), 0.16);
  }
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? "visible" : "hidden")};
  transform: ${(props) =>
    props.$isOpen ? "translateY(0) scale(1)" : "translateY(-8px) scale(0.97)"};
  pointer-events: ${(props) => (props.$isOpen ? "auto" : "none")};
  transition:
    opacity 220ms var(--ease-snappy),
    transform 260ms var(--ease-snappy);
  -webkit-overflow-scrolling: touch;
  animation: ${(p) => (p.$isOpen ? panelFloat : "none")} 4.8s ease-in-out
    infinite;

  /* Top specular reflection on the panel — real white so the highlight
     stays visible in dark theme. */
  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 24px;
    right: 24px;
    height: 18px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.85) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(2px);
    pointer-events: none;
    z-index: 3;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  [data-theme="dark"] &::before {
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.18) 0%,
      rgba(255, 255, 255, 0) 100%
    );
  }

  /* Bottom fade when inner scroll overflows (toggled via $faded prop) */
  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 36px;
    background: linear-gradient(
      to bottom,
      transparent,
      rgba(var(--glass-pale-rgb), 0.95)
    );
    pointer-events: none;
    border-radius: 0 0 22px 22px;
    transition: opacity 0.2s ease;
    opacity: ${({ $faded }) => ($faded ? 1 : 0)};
  }

  /* Scrollbar styling for the inner scroll container */
  /* div {
    &::-webkit-scrollbar {
      width: 6px;
    }
    &::-webkit-scrollbar-track {
      border-radius: 20px;
      background: rgba(var(--color-brand-300-rgb), 0.08);
    }
    &::-webkit-scrollbar-thumb {
      cursor: grab;
      background: rgba(var(--color-brand-400-rgb), 0.38);
      border-radius: 20px;
    }
    &::-webkit-scrollbar-thumb:hover {
      background: rgba(var(--color-brand-400-rgb), 0.58);
    }
    &::-webkit-scrollbar-thumb:active {
      background: rgba(var(--color-brand-600-rgb), 0.7);
      cursor: grabbing;
    }
  } */
`;

// ─── Items ──────────────────────────────────────────────────────────────────

export const DropdownItem = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 14px;
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: ${(p) => (p.$active ? 600 : 500)};
  line-height: normal;
  letter-spacing: -0.005em;
  white-space: nowrap;
  color: ${(p) =>
    p.$active ? "var(--color-brand-700)" : "var(--color-text-body)"};

  [data-theme="dark"] & {
    color: ${(p) =>
      p.$active ? "var(--color-text-strong)" : "var(--color-text-strong)"};
  }
  opacity: ${(p) => (p.$noClick ? 0.45 : 1)};
  cursor: ${(p) => (p.$noClick ? "default" : "pointer")};
  transition:
    background 180ms var(--ease-snappy),
    color 180ms var(--ease-snappy),
    transform 180ms var(--ease-snappy);

  svg {
    path {
      fill: var(--color-brand-400);
    }
  }

  /* Active item: soft brand-tinted fill + left indicator bar.
     Suppressed on $noClick so header/label rows stay clean. */
  ${(p) =>
    p.$active &&
    !p.$noClick &&
    `
      background: linear-gradient(
        90deg,
        rgba(var(--color-brand-300-rgb), 0.32) 0%,
        rgba(var(--color-brand-300-rgb), 0.1) 100%
      );
      &::before {
        content: "";
        position: absolute;
        left: 4px;
        top: 14px;
        bottom: 14px;
        width: 3px;
        border-radius: 999px;
        background: linear-gradient(180deg, var(--color-brand-400) 0%, var(--color-brand-600) 100%);
        box-shadow: 0 0 6px rgba(var(--color-brand-600-rgb), 0.4);
      }
`}

  /* $noClick = read-only label (e.g. "Filter By" header) — subdued style. */
  ${(p) =>
    p.$noClick &&
    `
      color: var(--color-text-muted);
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      padding: 10px 14px 6px;
      opacity: 0.8;
    `}

  ${(p) =>
    p.$disabled &&
    `
      opacity: 0.45;
      cursor: not-allowed;
    `}

  &:hover {
    ${(p) =>
      !p.$noClick &&
      !p.$active &&
      `
        background: linear-gradient(
          90deg,
          rgba(var(--color-brand-300-rgb), 0.22) 0%,
          rgba(var(--color-brand-300-rgb), 0.06) 100%
        );
        color: var(--color-text-strong);
        transform: translateX(2px);
      `}
  }
`;

export const DropdownItemActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;

  svg path {
    stroke: var(--color-brand-600);
  }
`;

export const DropdownItemActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border-radius: 8px;
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.32);
  background: var(--glass-base-2);
  color: var(--color-brand-700);
  cursor: pointer;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition:
    transform 160ms var(--ease-snappy),
    background 160ms var(--ease-snappy),
    border-color 160ms var(--ease-snappy),
    color 160ms var(--ease-snappy);

  svg {
    display: block;
  }

  svg path {
    stroke: currentColor;
  }

  &:hover {
    transform: scale(1.06);
    background: var(--glass-base-1);
    border-color: rgba(var(--color-brand-400-rgb), 0.5);
    color: var(--color-brand-700);
  }

  &:active {
    transform: scale(0.96);
  }
`;

export const DisturbingCard = styled.div`
  display: flex;
  height: 18px;
  padding: 2px 6px;
  align-items: center;
  gap: 3px;
  border-radius: 999px;
  border: 1px solid rgba(var(--glass-white-rgb), 0.5);
  background: linear-gradient(
    180deg,
    var(--color-brand-400) 0%,
    var(--color-brand-600) 100%
  );
  color: var(--glass-text-on-pill);
  font-weight: 700;
  font-size: 10px;
  font-family: var(--font-family-primary);
  letter-spacing: 0.04em;
  box-shadow:
    0 3px 8px rgba(var(--color-brand-600-rgb), 0.32),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);

  svg path {
    stroke: #ffffff;
  }
`;
